import React, { useEffect } from 'react';
import DetailsForm from 'components/DetailsPageBoilerplate/DetailsForm';
import './_Style.scss';
import {
  AdminType,
  CompanyType,
  Details,
  DetailsResources,
  DetailsViewModel,
  SaveData,
  InitialDetailsDataStructure,
} from 'components/DetailsPageBoilerplate/types';
import { useDispatch } from 'react-redux';
import { Heading } from 'lmc-ui-library-rebrand';
import { resetContainerWidth, setContainerWidth } from 'store/layout/actions';
import { ContainerWidth } from 'store/layout/types';
import Cycle from 'components/Cycle/Cycle';
import { ReducerDataState } from 'shared/interface/globalInterface';
import Spinner from 'shared/Spinner/Spinner';
import { formatFullAddressOld } from 'utils/address/index';
import { clubsToResourceData } from 'utils/api/resources/mapper';
import { resourceDataToOptionMapper } from 'utils/forms/mapper';
import { format } from 'utils/date';
import { OldAddress } from 'domain/Address';

type Props = {
  type: 'Network' | 'Club' | 'Firm';
  containerWidth: ContainerWidth;
  fetchedDetails: ReducerDataState<InitialDetailsDataStructure>;
  onCompanyUpdate: (id: string, values: SaveData) => void;
  onAdminUpdate: (id: string, values: SaveData) => void;
};

const formatAddress = (address: OldAddress) => ({
  postcode: address.postcode,
  city: address.city,
  addressLine1: address.addressLine1 || '',
  addressLine2: address.addressLine2 || '',
  county: address.county || '',
  houseNumber: address.houseNumber,
});

const adminDataToViewModel = (data: AdminType): DetailsViewModel => {
  const fullAddress: string = formatFullAddressOld(data.address);

  const details: Details[] = [
    { label: 'Admin name', name: 'name', value: `${data.firstName} ${data.lastName}` },
    { label: 'FCA Number', name: 'fca_number', value: data.fcaNumber ? data.fcaNumber.toString() : undefined },
  ].filter((detail) => detail.value !== undefined);

  return {
    data: {
      id: data.id,
      contactNumber: data.phone,
      fullAddress,
      address: formatAddress(data.address),
      email: data.email,
      isLMCActive: data.lmcStatus,
      isFCAActive: data.fcaStatus,
    },
    details,
    error: '',
    isSaved: false,
  };
};

const companyDataToViewModel = (type: string, data: CompanyType, resources: DetailsResources): DetailsViewModel => {
  const fullAddress: string = formatFullAddressOld(data.address);

  const details: Details[] = [
    { label: `${type} Name`, name: 'name', value: data.name },
    { label: 'FCA Number', name: 'fca_number', value: (data.fcaNumber || '').toString() },
  ];

  if (type === 'Firm') {
    if (data.network) {
      details.push({
        label: 'Network',
        name: 'network',
        value: data.network,
      });
    } else {
      details.push({
        label: 'Submission route',
        name: 'submissionRoute',
        value: data.submissionRoute || { value: '', option: '' },
        options: resources.clubs ? resourceDataToOptionMapper(clubsToResourceData(resources.clubs)) : [],
      });
    }
  }

  details.push({
    label: 'Date registered',
    name: 'registered_at',
    value: format(data.registeredAt).fromBackendToView(),
  });

  const filteredDetails = details.filter((detail) => detail.value);

  return {
    isSaved: false,
    error: '',
    details: filteredDetails,
    data: {
      fullAddress,
      isFCAActive: data.fcaStatus,
      isLMCActive: data.lmcStatus,
      email: data.email,
      contactNumber: data.phone,
      address: formatAddress(data.address),
      id: data.id,
    },
  };
};

const renderDetails = (
  type: string,
  data: InitialDetailsDataStructure,
  companySubmitHandler: any,
  adminSubmitHandler: any,
) => {
  const adminsViewModel: DetailsViewModel[] = data.admins.map((admin) => adminDataToViewModel(admin));
  const companyViewModel: DetailsViewModel = companyDataToViewModel(type, data.company, data.resources);

  return (
    <>
      <div className="forms__column">
        <Heading mode="title" weight="bold" className="heading">
          {type} Details
        </Heading>
        <DetailsForm
          details={companyViewModel.details}
          data={companyViewModel.data || {}}
          onSubmit={companySubmitHandler(companyViewModel.data ? companyViewModel.data.id : '')}
          error={companyViewModel.error}
          isSaved={companyViewModel.isSaved}
        />
      </div>
      <div className="forms__column">
        <Heading mode="title" weight="bold" className="heading">
          Admin details
        </Heading>
        {adminsViewModel.length > 0 ? (
          <Cycle>
            {adminsViewModel.map((adminViewModel) => (
              <DetailsForm
                key={`admin--id--${adminViewModel.data ? adminViewModel.data.id : ''}`}
                details={adminViewModel.details}
                data={adminViewModel.data || {}}
                onSubmit={adminSubmitHandler(adminViewModel.data ? adminViewModel.data.id : '')}
                error={adminViewModel.error}
                isSaved={adminViewModel.isSaved}
              />
            ))}
          </Cycle>
        ) : (
          <div className="forms__column--info">This {type} have no admins.</div>
        )}
      </div>
    </>
  );
};

const DetailsPageBoilerplate: React.FC<Props> = ({
  type,
  fetchedDetails,
  containerWidth,
  onCompanyUpdate,
  onAdminUpdate,
}: Props) => {
  const dispatch = useDispatch();
  const data: InitialDetailsDataStructure = fetchedDetails.data;

  useEffect(() => {
    dispatch(setContainerWidth(containerWidth));

    return () => {
      dispatch(resetContainerWidth());
    };
  }, [containerWidth, dispatch]);

  const companySubmitHandler = (companyId: string) => {
    return (values: SaveData) => {
      onCompanyUpdate(companyId, values);
    };
  };

  const adminSubmitHandler = (adminId: string) => {
    return (values: SaveData) => {
      onAdminUpdate(adminId, values);
    };
  };

  return (
    <div className="forms">
      {fetchedDetails.pending && (
        <div>
          <Spinner />
        </div>
      )}
      {fetchedDetails.error && <div>{fetchedDetails.error}</div>}
      {!fetchedDetails.pending &&
        !fetchedDetails.error &&
        renderDetails(type, data, companySubmitHandler, adminSubmitHandler)}
    </div>
  );
};

export default DetailsPageBoilerplate;
